import { useMutation, useQuery, useQueryClient } from 'react-query'

import type { PoleasPublicBasicContacts, PoleasPublicSignInOptions, PoleasPublicWizardLoginInfo } from '@/dtos'
import type { AxiosError } from 'axios'
import { longStaleTime } from 'BrokerTsLib/src/hooks'
import apiClient from 'BrokerTsLib/src/Utils/apiClient'
import { useUrl } from '.'
import { Apis } from '../appSettings/AppSettings'

export const useGetBasicContacts = () => {
  const url = useUrl(Apis.poleasPublic)

  return useQuery({
    queryKey: ['useGetBasicContacts'],
    queryFn: async () => {
      return await apiClient.get<PoleasPublicBasicContacts>(`${url}/GetMe`)
    },
    staleTime: longStaleTime,
  })
}

export const useGetLoginInfo = (token?: string) => {
  const url = useUrl(Apis.poleasPublic)

  return useQuery({
    queryKey: ['useGetLoginInfo', token],
    queryFn: async () => {
      return await apiClient.get<PoleasPublicWizardLoginInfo>(`${url}/GetLoginInfo?token=${token}`)
    },
    staleTime: longStaleTime,
  })
}

export const usePublicSignIn = () => {
  const url = useUrl(Apis.poleasPublic)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn:({ token, data }: {
      token: string
      data: PoleasPublicSignInOptions
    }) => apiClient.post<PoleasPublicBasicContacts>(`${url}/PublicSignIn?token=${token}`, data),
    onSuccess: () => {
      queryClient.setQueriesData(['isLogged'], { logged: true })
    }
  })
}

export const useSaveBasicContacts = () => {
  const url = useUrl(Apis.poleasPublic)

  return useMutation({
    mutationFn: (data: PoleasPublicBasicContacts) =>
      apiClient.post(`${url}/SaveBasicContacts`, data),
  })
}

export const usePublicSignOut = () => {
  const url = useUrl(Apis.poleasPublic)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.post(`${url}/PublicSignOut`).then(res => res.data),
    onSuccess: () => {
      queryClient.setQueriesData(['isLogged'], { logged: false })
    }
  })
}

export const usePublicIsLogged = () => {
  const url = useUrl(Apis.poleasPublic)

  return useQuery<{ logged: boolean }, AxiosError>({
    queryKey: ['isLogged'],
    queryFn: () => {
      return apiClient.get<{ logged: boolean }>(`${url}/PublicIsLogged`).then(res => res.data)
    },
    retry: false,
    refetchInterval: 1000 * 60 * 5,
  })
}

export const useGenerateToken = () => {
  const url = useUrl(Apis.poleasPublic)

  return useMutation({
    mutationFn: (data: { paramsId: string }) => apiClient.post<string>(`${url}/GenerateToken`, data).then(res => res.data),
  })
}